import { Box, IconButton } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  clearReplacementWorkAgreementData,
  selectReplacementWorkAgreementData,
  setReplacementWorkAgreementData,
} from "../../../../../app/appSlice";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { routes } from "../../../../../app/paths";
import { CustomButton } from "../../../../../common/components/functional/customButton/CustomButton";
import OverlaySpinner from "../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { Heading3 } from "../../../../../common/components/styled/styled";
import { getName } from "../../../../../common/helpers/common";
import { ReactComponent as ArrowLeft } from "../../../../../common/icons/arrowLeft.svg";
import { Nullable } from "../../../../../common/types/nullable";
import useIsLoading from "../../../../../hooks/useIsLoading";
import useNotification from "../../../../../hooks/useNotification";
import { FormContainer } from "../../../../customer/customerManagement/styles";
import { TargetedShiftSpec } from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesApi";
import { fetchTalent } from "../../../../talents/talentsSlice";
import OrdersHeader from "../../../OrdersHeader";
import {
  WORK_AGREEMENT_ACCEPTED,
  WORK_AGREEMENT_DECLINED,
} from "../../../constants";
import { WorkAgreementInfo } from "../../../orderAPI";
import {
  canCancelEmployment,
  fetchOrderRowsByBundleId,
  fetchWorkAgreement,
  fetchWorkAgreementInfo,
  selectOrderRowById,
} from "../../../orderRowsDetailedSlice";
import { SelectedTalent } from "../../../types/editOrder";
import { WorkAgreementForm } from "./WorkAgreementForm";
import TalentProfileLinkButton from "./components/TalentProfileLinkButton";
import {
  emptyFormValues,
  FormKeys,
  FormSchedulingClassIds,
  getWorkAgreementFormDefaultValues,
  getWorkAgreementFormValues,
  WorkAgreementFormValues,
  WorkOfferType,
} from "./helpers";

export const WorkAgreement = () => {
  const [talent, setTalent] = useState<Nullable<SelectedTalent>>(null);
  const { bundleId, workAgreementId } = useParams();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { sendNotification } = useNotification();

  // TODO: causes a crash if the view is navigated directly from the browser's address bar
  const location = useLocation();
  const selectedTalent: SelectedTalent = location.state?.talent;
  if (selectedTalent && talent !== selectedTalent) {
    setTalent(selectedTalent);
  }
  const type = location.state?.type ?? WorkOfferType.New;

  const replacementWorkAgreementData = useAppSelector(
    selectReplacementWorkAgreementData,
  );

  // Track scheduling class ids in a separate state for ease of management:
  // work agreement form uses schedulingClassId field for both monthly and hourly scheduling class ids,
  // but they both need to be available so that the user can switch between them.
  // To keep values correct and concise, ids are kept here and passed down.
  const [schedulingClassIds, setSchedulingClassIds] =
    useState<FormSchedulingClassIds>({
      hourlySchedulingClassId: "",
      monthlySchedulingClassId: "",
    });

  const sourceEmployment =
    type === WorkOfferType.New ? undefined : location.state?.employment;
  // TODO not first assignment I guess
  const sourceAssignment = sourceEmployment?.assignments[0];

  const [workAgreementInfo, setWorkAgreementInfo] =
    useState<WorkAgreementInfo | null>(null);

  const [defaultValues, setDefaultValues] =
    useState<WorkAgreementFormValues>(emptyFormValues);
  const [version, setVersion] = useState(0);
  const [targetedShiftSpec, setTargetedShiftSpec] =
    useState<TargetedShiftSpec | null>(null);
  const [systemUploadProcessed, setSystemUploadProcessed] = useState(false);
  const hasSystemGeneratedDocument =
    systemUploadProcessed && !!defaultValues.workAgreementDocumentId;
  const canReplaceWorkAgreement = useMemo(() => {
    return (
      defaultValues.status === "ACCEPTED" &&
      (!defaultValues.workAgreementDocumentId || hasSystemGeneratedDocument)
    );
  }, [
    defaultValues.status,
    defaultValues.workAgreementDocumentId,
    hasSystemGeneratedDocument,
  ]);

  const isLoading = useIsLoading(["orderRowsDetailed"]);

  const order = useAppSelector((state) =>
    selectOrderRowById(state, bundleId!!),
  );

  const handleReplaceWorkAgreement = () => {
    if (!bundleId || !talent) return;
    dispatch(canCancelEmployment({ bundleId, talentId: talent.id })).then(
      (action) => {
        if (isFulfilled(action)) {
          const canCancel = action.payload;
          if (canCancel) {
            // Save current form data before navigation
            dispatch(
              setReplacementWorkAgreementData({
                formValues: {
                  ...defaultValues,
                  [FormKeys.Status]: "NEW",
                  [FormKeys.ReplaceWorkAgreementId]: workAgreementId!,
                },
                talent: talent,
                targetedShiftTargetingRules:
                  targetedShiftSpec?.targetingRules ?? [],
              }),
            );
            // Navigate to new work agreement form
            navigate(routes.workOffer(bundleId));
          } else {
            sendNotification(
              "order:cannotReplaceWorkAgreement",
              "order:cannotReplaceWorkAgreementInfo",
            );
          }
        }
      },
    );
  };

  useEffect(() => {
    if (!order) {
      dispatch(fetchOrderRowsByBundleId({ id: bundleId }));
    }
  }, [order, bundleId, dispatch]);

  useEffect(() => {
    if (workAgreementId) {
      // old work agreement
      dispatch(fetchWorkAgreement(workAgreementId)).then((action) => {
        if (isFulfilled(action)) {
          const { status, version } = action.payload;
          const values = getWorkAgreementFormValues(action.payload);
          setDefaultValues(values);
          setVersion(version);
          const topLevelRules =
            action.payload.targetedShiftTargetingRules ?? [];

          setTargetedShiftSpec(
            topLevelRules.length
              ? {
                  version: 0,
                  variationValidationAssignmentId: null,
                  targetingRules: topLevelRules,
                  salaries: values[FormKeys.TargetedShiftSalarySpecs],
                }
              : null,
          );

          // set talent if user navigates directly to the page (selectedTalent is not passed)
          if (!selectedTalent) {
            dispatch(
              fetchTalent({ talentId: action.payload.workAgreement.talentId }),
            ).then((action) => {
              if (isFulfilled(action)) {
                setTalent({
                  id: action.payload.id,
                  fullName: getName(action.payload.account, "firstNameFirst"),
                  displayName: getName(
                    action.payload.account,
                    "firstNameFirst",
                  ),
                });
              }
            });
          }

          const isReadOnly =
            status === WORK_AGREEMENT_ACCEPTED ||
            status === WORK_AGREEMENT_DECLINED;
          if (!isReadOnly) {
            // Get hourly and monthly scheduling class ids so that the user
            // can switch between them in the form.
            dispatch(fetchWorkAgreementInfo(bundleId!)).then((action) => {
              if (isFulfilled(action)) {
                const { schedulingClassId, monthlySchedulingClassId } =
                  action.payload;
                setSchedulingClassIds({
                  hourlySchedulingClassId: schedulingClassId,
                  monthlySchedulingClassId,
                });
              }
            });
          } else {
            // Use existing value only for display.
            setSchedulingClassIds({
              hourlySchedulingClassId: values.schedulingClassId,
              monthlySchedulingClassId: values.schedulingClassId,
            });
          }
        }
      });
    } else {
      // new work agreement or adding assignments
      if (bundleId) {
        // Check if we have replacement data from a replacement operation
        if (replacementWorkAgreementData) {
          setDefaultValues(replacementWorkAgreementData.formValues);
          setTalent(replacementWorkAgreementData.talent);
          setVersion(0);
          const rules =
            replacementWorkAgreementData.targetedShiftTargetingRules ?? [];
          if (rules.length) {
            setTargetedShiftSpec({
              version: 0,
              variationValidationAssignmentId: null,
              targetingRules: rules,
              salaries:
                replacementWorkAgreementData.formValues[
                  FormKeys.TargetedShiftSalarySpecs
                ] ?? [],
            });
          }
          return;
        }

        // if no bundle it should be an error?
        dispatch(fetchWorkAgreementInfo(bundleId)).then((action) => {
          if (isFulfilled(action)) {
            setWorkAgreementInfo(action.payload);
            setSchedulingClassIds({
              hourlySchedulingClassId: action.payload.schedulingClassId,
              monthlySchedulingClassId: action.payload.monthlySchedulingClassId,
            });
            const rules = action.payload.targetedShiftTargetingRules;
            setTargetedShiftSpec(
              rules?.length
                ? {
                    version: 0,
                    variationValidationAssignmentId: null,
                    targetingRules: rules,
                    salaries: [],
                  }
                : null,
            );
            setVersion(0);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleId, workAgreementId, dispatch]);

  const refreshDefaultValues = () => {
    if (type === WorkOfferType.New && workAgreementInfo) {
      const values = getWorkAgreementFormDefaultValues(workAgreementInfo);
      if (values !== defaultValues) setDefaultValues(values);
    } else {
      if (sourceAssignment && workAgreementInfo) {
        const values = getWorkAgreementFormDefaultValues(
          workAgreementInfo,
          sourceEmployment,
          sourceAssignment,
        );
        if (values !== defaultValues) setDefaultValues(values);
      }
    }
  };

  useEffect(() => {
    refreshDefaultValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workAgreementInfo, sourceEmployment, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearReplacementWorkAgreementData());
    };
  }, [dispatch]);

  const navigateToOrderDetails = () => {
    navigate(`/home/orders/${bundleId}`);
  };

  const head =
    type === WorkOfferType.New
      ? replacementWorkAgreementData
        ? t("order:replacementWorkAgreement")
        : t("order:workOffer")
      : t("order:workAssignment");
  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      <Box display="flex" alignItems="center">
        <IconButton onClick={navigateToOrderDetails}>
          <ArrowLeft style={{ width: "24px", height: "24px" }} />
        </IconButton>
        <OrdersHeader
          title={order?.title || order?.orderRowTemplateName}
          customerCompany={order?.customerOrgName}
          orderRowCount={order?.orderRows.length}
        />
      </Box>
      <Box
        sx={{
          maxWidth: "1100px",
          backgroundColor: "#EAEAF1",
          borderRadius: "12px",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          p={2}
          pr={0}
          gap="12px"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap="12px">
            <Heading3>{head}</Heading3>
            {(talent?.id || selectedTalent?.id) && (
              <TalentProfileLinkButton
                talentName={talent?.fullName || selectedTalent?.fullName}
                id={talent?.id || selectedTalent?.id}
              />
            )}
          </Box>
          {canReplaceWorkAgreement && (
            <CustomButton
              variant="outlined"
              sx={{ height: "36px", borderRadius: "20px" }}
              onClick={handleReplaceWorkAgreement}
            >
              {t("order:replaceAcceptedWorkAgreement")}
            </CustomButton>
          )}
        </Box>
        {!isLoading && (
          <FormContainer>
            <WorkAgreementForm
              bundleId={bundleId!}
              talent={selectedTalent || talent}
              defaultValues={defaultValues}
              version={version}
              navigateToOrderDetails={navigateToOrderDetails}
              type={type}
              sourceEmployment={sourceEmployment}
              schedulingClassIds={schedulingClassIds}
              targetedShiftSpec={targetedShiftSpec}
              setHasSystemGeneratedDocument={setSystemUploadProcessed}
              hasSystemGeneratedDocument={hasSystemGeneratedDocument}
            />
          </FormContainer>
        )}
      </Box>
    </>
  );
};
